import { AxiosResponse } from 'axios';

import { useQueryClient } from '@tanstack/react-query';

import { axiosInstance } from '~/services/fetchServiceV2';
import { TypeCodeState } from '~/types';
import { TypeErrorFormat, requestHelper } from '~/utils/fetchHelper';

type TypeUserLatestContractResponse = {
  code: string;
  message: string;
  data: TypeUserLatestContract;
};

export type TypeUserLatestContract = {
  /** 근로계약 아이디 */
  laborContractId: number | null;
  /** 클러스터 */
  cluster: TypeCodeState | null;
  /** 센터 */
  center: TypeCodeState | null;
  /** 업무 파트 */
  workPart: TypeCodeState | null;
  /** 팀명 */
  teamName: string | null;
  /** 근무 시프트 */
  workTimeShift: string | null;
  /** 계약 일자 */
  contractDate: string | null;
  /**
   * 근로 계약 상태
   * @remarks
   * - REGISTERED(등록)
   * - SIGNED(서명)
   * - CANCELLED(취소)
   * - CANCELLED_BY_ADMIN(관리자 취소)
   * */
  contractStatus: TypeCodeState | null;
  /**
   * 재계약 가능 상태
   * @remarks
   * - ENABLE(가능)
   * - DISABLE(불가능)
   * */
  renewalStatus: TypeCodeState | null;
};

/**
 * @description 사용자의 최신 근로 계약 정보를 조회하는 커스텀훅
 * @remarks 일용직의 계약 정보 표시 및 출근 버튼 클릭 시 계약 가능 여부 확인에 사용
 */
export const useGetUserLatestContract = () => {
  const queryClient = useQueryClient();

  const requestUserLatestContract = async ({
    onSuccess,
    onError,
    showLoading = true,
  }: {
    onSuccess: (response: TypeUserLatestContractResponse) => void;
    onError: (error: TypeErrorFormat) => void;
    showLoading?: boolean;
  }) => {
    const endpoint = '/labor/contracts/latest';
    const query = queryClient.ensureQueryData<AxiosResponse<TypeUserLatestContractResponse>>({
      queryKey: ['contracts', 'latest'],
      queryFn: () => axiosInstance.get<TypeUserLatestContractResponse>(endpoint),
      gcTime: 0,
    });

    return requestHelper(() => query, onSuccess, onError, undefined, { showLoading });
  };

  return requestUserLatestContract;
};
